// SPDX-License-Identifier: AGPL-3.0-or-later

import {WORKER_CACHE_PREFIX} from '@app/features/platform/service_worker/WorkerCacheCleanup';

export const PENDING_NAVIGATION_CACHE_NAME = `${WORKER_CACHE_PREFIX}-pending-navigation`;
export const PENDING_NAVIGATION_CACHE_KEY = '/pending-notification-nav';
export const PENDING_NAVIGATION_TTL_MS = 60_000;

export const NOTIFICATION_CLICK_NAVIGATE_TYPE = 'NOTIFICATION_CLICK_NAVIGATE';
export const CLAIM_PENDING_NAVIGATION_TYPE = 'CLAIM_PENDING_NAVIGATION';
export const PENDING_NAVIGATION_TYPE = 'PENDING_NAVIGATION';

export interface PendingNavigationEntry {
	readonly id: string;
	readonly url: string;
	readonly targetUserId?: string;
	readonly createdAt: number;
}

export interface PendingNavigationStore {
	readonly caches: CacheStorage | undefined;
	readonly origin: string;
	readonly now?: () => number;
}

export interface NotificationNavigateMessage {
	readonly type: typeof NOTIFICATION_CLICK_NAVIGATE_TYPE | typeof PENDING_NAVIGATION_TYPE;
	readonly entry: PendingNavigationEntry;
}

export interface NotificationNavIngestState {
	readonly seenIds: Set<string>;
	queued: PendingNavigationEntry | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

function createPendingNavigationId(): string {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readPendingNavigationFields(raw: Record<string, unknown>): {
	id: string | null;
	url: string | null;
	createdAt: number | null;
	targetUserId: string | undefined;
} {
	const id = typeof raw.id === 'string' && raw.id.length > 0 ? raw.id : null;
	const url = typeof raw.url === 'string' && raw.url.length > 0 ? raw.url : null;
	const createdAt = typeof raw.createdAt === 'number' && Number.isFinite(raw.createdAt) ? raw.createdAt : null;
	const targetUserId =
		typeof raw.targetUserId === 'string' && raw.targetUserId.length > 0 ? raw.targetUserId : undefined;
	return {id, url, createdAt, targetUserId};
}

export function createPendingNavigationEntry(input: {
	url: string;
	targetUserId?: string;
	createdAt?: number;
	id?: string;
}): PendingNavigationEntry {
	const entry: PendingNavigationEntry = {
		id: input.id ?? createPendingNavigationId(),
		url: input.url,
		createdAt: input.createdAt ?? Date.now(),
	};
	if (input.targetUserId) {
		return {...entry, targetUserId: input.targetUserId};
	}
	return entry;
}

export function isPendingNavigationExpired(entry: PendingNavigationEntry, now: number): boolean {
	return now - entry.createdAt > PENDING_NAVIGATION_TTL_MS;
}

export function parsePendingNavigation(raw: unknown): PendingNavigationEntry | null {
	if (!isRecord(raw)) {
		return null;
	}
	const fields = readPendingNavigationFields(raw);
	if (!fields.id || !fields.url || fields.createdAt === null) {
		return null;
	}
	return createPendingNavigationEntry({
		id: fields.id,
		url: fields.url,
		targetUserId: fields.targetUserId,
		createdAt: fields.createdAt,
	});
}

export function livePendingEntry(stored: unknown, now: number): PendingNavigationEntry | null {
	const entry = parsePendingNavigation(stored);
	if (!entry || isPendingNavigationExpired(entry, now)) {
		return null;
	}
	return entry;
}

export function pendingNavigationBroadcastMessage(entry: PendingNavigationEntry): Record<string, unknown> {
	return pendingNavigationPayload(NOTIFICATION_CLICK_NAVIGATE_TYPE, entry);
}

export function pendingNavigationClaimReply(entry: PendingNavigationEntry | null): Record<string, unknown> {
	if (!entry) {
		return {type: PENDING_NAVIGATION_TYPE};
	}
	return pendingNavigationPayload(PENDING_NAVIGATION_TYPE, entry);
}

export function parseNotificationNavigateMessage(data: unknown): NotificationNavigateMessage | null {
	if (!isRecord(data)) {
		return null;
	}
	if (data.type !== NOTIFICATION_CLICK_NAVIGATE_TYPE && data.type !== PENDING_NAVIGATION_TYPE) {
		return null;
	}
	const fields = readPendingNavigationFields(data);
	if (!fields.url) {
		return null;
	}
	return {
		type: data.type,
		entry: createPendingNavigationEntry({
			id: fields.id ?? undefined,
			url: fields.url,
			targetUserId: fields.targetUserId,
			createdAt: fields.createdAt ?? undefined,
		}),
	};
}

export function createNotificationNavIngestState(): NotificationNavIngestState {
	return {seenIds: new Set(), queued: null};
}

export function ingestNotificationNav(
	state: NotificationNavIngestState,
	entry: PendingNavigationEntry,
	isAuthenticated: boolean,
): PendingNavigationEntry | null {
	if (state.seenIds.has(entry.id)) {
		return null;
	}
	state.seenIds.add(entry.id);
	if (!isAuthenticated) {
		state.queued = entry;
		return null;
	}
	return entry;
}

export function takeQueuedNotificationNav(state: NotificationNavIngestState): PendingNavigationEntry | null {
	const queued = state.queued;
	state.queued = null;
	return queued;
}

function pendingNavigationPayload(type: string, entry: PendingNavigationEntry): Record<string, unknown> {
	const message: Record<string, unknown> = {
		type,
		id: entry.id,
		url: entry.url,
	};
	if (entry.targetUserId) {
		message.targetUserId = entry.targetUserId;
	}
	return message;
}

function cacheRequest(origin: string): Request {
	return new Request(new URL(PENDING_NAVIGATION_CACHE_KEY, origin).toString());
}

export async function recordPendingNavigation(
	store: PendingNavigationStore,
	entry: PendingNavigationEntry,
): Promise<void> {
	if (!store.caches) {
		return;
	}
	const cache = await store.caches.open(PENDING_NAVIGATION_CACHE_NAME);
	const body = JSON.stringify(entry);
	await cache.put(cacheRequest(store.origin), new Response(body, {headers: {'content-type': 'application/json'}}));
}

export async function claimPendingNavigation(store: PendingNavigationStore): Promise<PendingNavigationEntry | null> {
	if (!store.caches) {
		return null;
	}
	const cache = await store.caches.open(PENDING_NAVIGATION_CACHE_NAME);
	const request = cacheRequest(store.origin);
	const cached = await cache.match(request);
	await cache.delete(request);
	if (!cached) {
		return null;
	}
	let raw: unknown;
	try {
		raw = await cached.json();
	} catch {
		return null;
	}
	const now = store.now?.() ?? Date.now();
	return livePendingEntry(raw, now);
}
